import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import QueryHandler from '@/components/GraphQL/QueryHandler';
import LiftCard from '@/components/LiftCard/LiftCard';
import CategoryIcon from '@/components/Images/CategoryIcon';
import { ForwardLink } from '@/components/Buttons/BackForwardButtons';
import { FormattedMessage } from 'react-intl';
import _get from 'lodash.get';
import QUERY_GET_POSTS_BY_CATEGORY from '@/graphql/wordpress/posts/queryGetPostsByCategory';
import QUERY_GET_PAGES_BY_CATEGORY from '@/graphql/wordpress/pages/queryGetPagesByCategory';

const Column = styled.div`
  margin-bottom: 64px;
`;

const Catlift = styled(LiftCard)`
  border-bottom: 1px solid
    ${(props) => props.theme.colors.kuluttaja2021.harmaa[20]};
  padding-bottom: 24px;
  &:last-of-type {
    margin-bottom: 24px;
  }
`;

/**
 * CategoryListPosts component for displaying a list of posts by category or magazine.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.category - The category or magazine object.
 * @param {number} props.post_count - Number of posts to display.
 * @returns {JSX.Element} - Rendered CategoryListPosts component.
 */
const CategoryListPosts = ({ category, post_count = 3 }) => {
  let query = QUERY_GET_POSTS_BY_CATEGORY;
  let termId;
  let taxonomy = 'CATEGORY';

  if (!category) {
    return null;
  }

  // If the category is a magazine, use the magazineId instead of the categoryId
  if (category.categoryId) {
    termId = category.categoryId.toString();

    // If the category is a testi category, use the pages query instead of the posts query
    if (category.slug.includes('testi')) {
      query = QUERY_GET_PAGES_BY_CATEGORY;
    }
  } else if (category.magazineId) {
    termId = category.magazineId.toString();
    taxonomy = 'MAGAZINE';
  }

  return (
    <Column>
      <h3>
        {category.icon && <CategoryIcon icon={category.icon} />} {category.name}
      </h3>
      <QueryHandler
        query={query}
        variables={{
          termId,
          count: post_count,
          taxonomy
        }}
        small_loader
      >
        {(data) => {
          const nodes = _get(data, 'pages.nodes') || _get(data, 'posts.nodes');
          if (!nodes) {
            return null;
          }
          return nodes.map((post, index) => (
            <Catlift
              image={post.featuredImage}
              key={`post-${post.id}`}
              title={post.title}
              date={post.dateGmt}
              size={index < 1 ? 2 : 3}
              content={index < 1 && post.excerpt ? post.excerpt : null}
              categories={post.categories}
              link={post.uri}
              restricted={post.paywall.premium}
            />
          ));
        }}
      </QueryHandler>
      <ForwardLink to={_get(category, 'category_page.uri')}>
        <FormattedMessage id="bodybuilder.categorylist.all" />
      </ForwardLink>
    </Column>
  );
};

CategoryListPosts.propTypes = {
  category: PropTypes.object,
  post_count: PropTypes.number
};

export default CategoryListPosts;
